import type { ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { useActiveCheckinSession } from '@/hooks/ba/useActiveCheckinSession';
import type { StoredActiveCheckinSession } from '@/lib/ba/activeShiftStorage';

const LoadingScreen = () => (
  <div className="flex h-screen items-center justify-center">Loading...</div>
);

export function RedirectIfActiveShift({ children }: { children: ReactNode }) {
  const { data: activeSession, isLoading } = useActiveCheckinSession();

  if (isLoading) return <LoadingScreen />;
  if (activeSession?.id) {
    return <Navigate to="/ba/active-shift" replace />;
  }

  return <>{children}</>;
}

export function RequireActiveShift({
  children,
  fallback = '/ba/campaigns',
}: {
  children: ReactNode;
  fallback?: string;
}) {
  const { data: activeSession, isLoading } = useActiveCheckinSession();

  if (isLoading) return <LoadingScreen />;
  if (!activeSession?.id) {
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
}

type LegacyWorkflowRedirectProps = {
  whenActive: (params: Record<string, string | undefined>, activeSession: StoredActiveCheckinSession) => string;
  whenInactive: (params: Record<string, string | undefined>) => string;
};

export function LegacyWorkflowRedirect({
  whenActive,
  whenInactive,
}: LegacyWorkflowRedirectProps) {
  const params = useParams<Record<string, string>>();
  const { data: activeSession, isLoading } = useActiveCheckinSession();

  if (isLoading) return <LoadingScreen />;

  return (
    <Navigate
      to={activeSession?.id ? whenActive(params, activeSession) : whenInactive(params)}
      replace
    />
  );
}
